import { useState, useEffect, useCallback } from 'react'
import { supabase, getUserHouseholdId, getHouseholdName, getHouseholdMemberProfiles } from '../lib/supabase'
import type { Dispatch, SetStateAction } from 'react'

export function useHousehold(userId: string | null | undefined): {
  householdId: string | null
  householdName: string | null
  householdLoading: boolean
  householdError: boolean
  memberCount: number
  partnerName: string | null
  membersChangedAt: number
  setHouseholdId: Dispatch<SetStateAction<string | null>>
  setHouseholdName: Dispatch<SetStateAction<string | null>>
  refreshMembers: (hId: string, uid: string) => Promise<void>
} {
  const [householdId,      setHouseholdId]      = useState<string | null>(null)
  const [householdName,    setHouseholdName]    = useState<string | null>(null)
  const [householdLoading, setHouseholdLoading] = useState(false)
  const [householdError,   setHouseholdError]   = useState(false)
  const [memberCount,      setMemberCount]      = useState(1)
  const [partnerName,      setPartnerName]      = useState<string | null>(null)
  const [membersChangedAt, setMembersChangedAt] = useState(0)

  useEffect(() => {
    if (!userId) {
      setHouseholdId(null)
      setHouseholdName(null)
      setHouseholdLoading(false)
      setHouseholdError(false)
      return
    }
    setHouseholdLoading(true)
    setHouseholdError(false)
    getUserHouseholdId(userId)
      .then(async id => {
        if (!id) { setHouseholdError(true); return }
        setHouseholdId(id)
        const name = await getHouseholdName(id)
        setHouseholdName(name)
      })
      .catch(() => setHouseholdError(true))
      .finally(() => setHouseholdLoading(false))
  }, [userId])

  const refreshMembers = useCallback(async (hId: string, uid: string) => {
    const profiles = await getHouseholdMemberProfiles(hId)
    setMemberCount(profiles.length)
    const partner = profiles.find(p => p.user_id !== uid)
    setPartnerName(partner?.display_name ?? null)
    setMembersChangedAt(Date.now())
  }, [])

  useEffect(() => {
    if (!householdId || !userId) { setMemberCount(1); setPartnerName(null); return }
    refreshMembers(householdId, userId)
  }, [householdId, userId, refreshMembers])

  useEffect(() => {
    if (!householdId || !userId) return
    const channel = supabase
      .channel(`hh-members:${householdId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'household_members',
        filter: `household_id=eq.${householdId}`,
      }, () => { refreshMembers(householdId, userId) })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [householdId, userId, refreshMembers])

  return {
    householdId,
    householdName,
    householdLoading,
    householdError,
    memberCount,
    partnerName,
    membersChangedAt,
    setHouseholdId,
    setHouseholdName,
    refreshMembers,
  }
}
